/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { HOME } from '@/constants/testIds';
import ShopSection from '@/components/ShopSection';
import GallerySection from '@/components/GallerySection';
import CustomRequestSection from '@/components/CustomRequestSection';
import WhoWeServeSection from '@/components/WhoWeServeSection';
import AboutSection from '@/components/AboutSection';
import FAQSection from '@/components/FAQSection';
import ReviewsSection from '@/components/ReviewsSection';
import TeeodoreSection from '@/components/TeeodoreSection';
import CommunityWallSection from '@/components/CommunityWallSection';
import TeeodoreCompanion from '@/components/TeeodoreCompanion';
import RecoveryAnnouncementModal from '@/components/RecoveryAnnouncementModal';

/**
 * Shirt Happens Studio — Sticky-scroll narrative hero.
 *
 * One tall scroll container ("track") with a sticky viewport.
 * As the user scrolls, a single normalized progress value (0..1) drives
 * five overlapping phases:
 *   Phase 1 — Hook headline reveal
 *   Phase 2 — Toilet thinker + thought bubble (lightbulb)
 *   Phase 3 — Sewer pipe draws across the page (SVG stroke)
 *   Phase 4 — Folded shirt ejects from pipe end with flash
 *   Phase 5 — Shirt center-stage with CTA
 */

const Phase1 = ({ progress }) => {
  // Phase 1: visible from 0 → ~0.22, then fade out. Starts fully visible.
  const opacity = useTransform(progress, [0, 0.18, 0.24], [1, 1, 0]);
  const y = useTransform(progress, [0, 0.22], [0, -60]);
  const wordPop = useTransform(progress, [0.02, 0.10], [0.85, 1]);

  return (
    <motion.div
      data-testid={HOME.phase1Hook}
      style={{ opacity, y }}
      className="absolute inset-0 flex items-start justify-center px-6 sm:px-12 pt-24 sm:pt-28 md:pt-32 pointer-events-none"
    >
      <div className="max-w-6xl text-left">
        <p className="font-body text-xs sm:text-sm tracking-[0.4em] uppercase text-brand-orange mb-6">
          A peer-reviewed truth →
        </p>
        <h1 className="font-display font-black leading-[0.92] tracking-[-0.02em] text-[10vw] sm:text-[8vw] md:text-[7rem] lg:text-[8.5rem] text-black">
          Studies show
          <br />
          most people get
          <br />
          their best ideas
          <br />
          <motion.span
            style={{ scale: wordPop, display: 'inline-block', transformOrigin: 'left center' }}
            className="bg-black text-white px-4 py-1 -skew-x-3 inline-block"
          >
            on the toilet.
          </motion.span>
        </h1>
        <p className="font-display font-bold text-2xl sm:text-3xl md:text-5xl mt-10 text-black/80">
          So we built a brand around it.
        </p>
      </div>
    </motion.div>
  );
};

const Phase2 = ({ progress }) => {
  // Phase 2: 0.18 → 0.42 (mascot slides up, bubble pops)
  const opacity = useTransform(progress, [0.18, 0.24, 0.42, 0.50], [0, 1, 1, 0]);
  const y = useTransform(progress, [0.18, 0.30], [120, 0]);
  const bubbleScale = useTransform(progress, [0.26, 0.34], [0, 1]);
  const bubbleOpacity = useTransform(progress, [0.26, 0.32, 0.46, 0.50], [0, 1, 1, 0]);
  const bulbScale = useTransform(progress, [0.30, 0.38, 0.44], [0.8, 1.15, 1]);
  const bulbGlow = useTransform(progress, [0.30, 0.40], [0, 1]);

  return (
    <motion.div
      style={{ opacity }}
      className="absolute inset-0 flex items-end justify-center sm:justify-start sm:pl-[8%] pb-[6vh] pointer-events-none"
    >
      <div className="relative flex items-end">
        {/* Mascot — Teeodore on his throne, having his classic hot idea */}
        <motion.img
          data-testid={HOME.phase2Mascot}
          src="/brand/teeodore-toilet.png"
          alt="Teeodore, Chief Shirt Officer, having a hot idea on the throne"
          style={{ y }}
          className="h-[52vh] sm:h-[62vh] md:h-[68vh] w-auto drop-shadow-[0_30px_30px_rgba(0,0,0,0.18)] select-none relative z-10"
          draggable="false"
        />

        {/* Thought bubble — hovers above Teeodore's tucked chin/fist */}
        <motion.div
          data-testid={HOME.phase2Bubble}
          style={{ scale: bubbleScale, opacity: bubbleOpacity }}
          className="absolute right-[68%] sm:right-[72%] top-[4%] sm:-top-4 origin-bottom-right z-20"
        >
          {/* Trailing dots leading down to his thinker fist */}
          <div className="absolute -bottom-10 right-3 flex flex-col gap-1.5 items-end">
            <span className="block w-3 h-3 rounded-full bg-white border-2 border-black" />
            <span className="block w-2 h-2 rounded-full bg-white border-2 border-black -mr-2" />
            <span className="block w-1.5 h-1.5 rounded-full bg-white border-2 border-black -mr-4" />
          </div>
          <div className="relative bg-white border-[3px] border-black rounded-[2.5rem] px-7 py-6 shadow-[8px_8px_0_#000]">
            <div className="flex items-center gap-3">
              <motion.div
                style={{ scale: bulbScale }}
                className="relative"
              >
                {/* Lightbulb SVG */}
                <svg width="58" height="58" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                  <motion.circle
                    cx="32" cy="28" r="22"
                    fill="#FFD23F"
                    style={{ opacity: bulbGlow }}
                  />
                  <path d="M32 8c-9.4 0-17 7.6-17 17 0 6.4 3.6 12 8.9 14.9V44c0 1.7 1.3 3 3 3h10.2c1.7 0 3-1.3 3-3v-4.1C45.4 37 49 31.4 49 25c0-9.4-7.6-17-17-17z" fill="#FF6600" stroke="#000" strokeWidth="3" strokeLinejoin="round"/>
                  <rect x="24" y="47" width="16" height="5" rx="1.5" fill="#000"/>
                  <rect x="26" y="54" width="12" height="4" rx="2" fill="#000"/>
                  <path d="M28 22c1.5-3 5.5-4 8-3" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
                {/* Sparkles */}
                <span className="absolute -top-2 -right-3 text-brand-orange font-display font-black text-lg">✦</span>
                <span className="absolute -bottom-1 -left-3 text-black font-display font-black text-sm">✦</span>
                <span className="absolute top-3 -left-5 text-black/60 text-xs">✦</span>
              </motion.div>
              <div className="font-display font-black text-2xl sm:text-3xl leading-tight text-black">
                Hot
                <br />
                <span className="text-brand-orange">idea!</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const Phase3 = ({ progress }) => {
  // Phase 3: pipe draws 0.40 → 0.70
  const opacity = useTransform(progress, [0.38, 0.44, 0.78, 0.86], [0, 1, 1, 0]);
  const pathLength = useTransform(progress, [0.42, 0.68], [0, 1]);
  const dashOffset = useTransform(progress, [0.42, 0.78], [0, -120]);

  return (
    <motion.div
      data-testid={HOME.phase3Pipe}
      style={{ opacity }}
      className="absolute inset-0 pointer-events-none"
    >
      <svg
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="pipeGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2a2a2a" />
            <stop offset="50%" stopColor="#0a0a0a" />
            <stop offset="100%" stopColor="#2a2a2a" />
          </linearGradient>
          <filter id="pipeGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* The pipe path: starts under-toilet (bottom-left area), snakes across, ends inside the viewport so the shirt visibly ejects from the pipe mouth */}
        {/* Outer pipe body (thick stroke) */}
        <motion.path
          d="M 240 820 C 240 720, 360 660, 520 660 S 820 760, 980 700 S 1240 540, 1340 560"
          fill="none"
          stroke="url(#pipeGrad)"
          strokeWidth="76"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pathLength }}
        />
        {/* Inner highlight */}
        <motion.path
          d="M 240 820 C 240 720, 360 660, 520 660 S 820 760, 980 700 S 1240 540, 1340 560"
          fill="none"
          stroke="#ff6600"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray="14 18"
          style={{ pathLength, strokeDashoffset: dashOffset }}
          opacity="0.85"
        />
        {/* Top metallic highlight */}
        <motion.path
          d="M 240 820 C 240 720, 360 660, 520 660 S 820 760, 980 700 S 1240 540, 1340 560"
          fill="none"
          stroke="#ffffff"
          strokeWidth="3"
          strokeLinecap="round"
          style={{ pathLength }}
          opacity="0.18"
          transform="translate(0,-20)"
        />

        {/* Joint rings along the pipe for industrial detail */}
        <circle cx="520" cy="660" r="46" fill="none" stroke="#000" strokeWidth="4" />
        <circle cx="980" cy="700" r="46" fill="none" stroke="#000" strokeWidth="4" />
        <circle cx="1240" cy="600" r="42" fill="none" stroke="#000" strokeWidth="4" />

        {/* Pipe mouth/opening at the end */}
        <ellipse cx="1340" cy="560" rx="26" ry="38" fill="#000" />
        <ellipse cx="1342" cy="560" rx="16" ry="26" fill="#1a0a00" />
      </svg>
    </motion.div>
  );
};

const Phase4 = ({ progress }) => {
  // Phase 4: shirt ejects 0.66 → 0.84, stays visible to end
  const opacity = useTransform(progress, [0.64, 0.70], [0, 1]);
  // Path: starts at pipe mouth (right of center), arcs up then lands above center
  const x = useTransform(progress, [0.66, 0.86], [300, 0]);
  const y = useTransform(progress, [0.66, 0.76, 0.86], [-30, -180, -140]);
  const rotate = useTransform(progress, [0.66, 0.86], [-25, 0]);
  const scale = useTransform(progress, [0.66, 0.72, 0.86], [0.4, 0.7, 0.75]);

  // Flash
  const flashOpacity = useTransform(progress, [0.64, 0.68, 0.74], [0, 1, 0]);
  const flashScale = useTransform(progress, [0.64, 0.74], [0.4, 2.2]);

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {/* Flash */}
      <motion.div
        style={{ opacity: flashOpacity, scale: flashScale }}
        className="absolute w-40 h-40 rounded-full bg-white mix-blend-screen"
        aria-hidden="true"
      >
        <div className="absolute inset-0 rounded-full bg-brand-orange/70 blur-2xl" />
      </motion.div>

      {/* Shirt */}
      <motion.img
        data-testid={HOME.phase4Shirt}
        src="/brand/folded-shirt.png"
        alt="Folded black t-shirt with SHIRT HAPPENS STUDIO wordmark"
        style={{ opacity, x, y, rotate, scale }}
        className="h-[36vh] sm:h-[44vh] w-auto drop-shadow-[0_30px_40px_rgba(0,0,0,0.35)] select-none"
        draggable="false"
      />
    </div>
  );
};

const Phase5 = ({ progress, onCtaClick }) => {
  // Phase 5: CTA + supporting copy 0.86 → 1.0
  const opacity = useTransform(progress, [0.84, 0.92], [0, 1]);
  const y = useTransform(progress, [0.84, 0.96], [60, 0]);

  return (
    <motion.div
      data-testid={HOME.phase5Cta}
      style={{ opacity, y }}
      className="absolute inset-x-0 bottom-[8vh] flex flex-col items-center px-6 text-center"
    >
      <p className="font-body text-xs sm:text-sm tracking-[0.4em] uppercase text-black/60 mb-4">
        Fresh out of the pipe →
      </p>
      <h2 className="font-display font-black text-3xl sm:text-5xl md:text-6xl tracking-[-0.02em] text-black mb-7 max-w-3xl leading-[1.02]">
        Turn your <span className="text-brand-orange">3am bathroom thought</span> into a tee.
      </h2>
      <button
        type="button"
        data-testid={HOME.ctaPrimary}
        onClick={onCtaClick}
        className="group inline-flex items-center gap-3 bg-black hover:bg-brand-orange text-white font-display font-bold text-base sm:text-lg uppercase tracking-wide px-8 sm:px-10 py-5 rounded-full transition-colors duration-300 shadow-[8px_8px_0_#FF6600] hover:shadow-[8px_8px_0_#000]"      >
        Turn Your Idea Into a Custom-Made Shirt
        <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
      </button>
    </motion.div>
  );
};

const ScrollNarrative = () => {
  const trackRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start start', 'end end'],
  });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 22, mass: 0.4 });

  // Background tint shifts subtly across phases
  const bgColor = useTransform(
    progress,
    [0, 0.25, 0.55, 0.85, 1],
    ['#FFF7EE', '#FFE6CF', '#1A1A1A', '#0A0A0A', '#FFF7EE']
  );
  const textInvert = useTransform(progress, [0.48, 0.55, 0.84, 0.90], [0, 1, 1, 0]);

  // Progress bar
  const barWidth = useTransform(progress, (v) => `${Math.min(100, Math.max(0, v * 100))}%`);

  return (
    <section
      ref={trackRef}
      data-testid={HOME.scrollSection}
      className="relative w-full"
      style={{ height: '500vh' }}
    >
      <motion.div
        style={{ backgroundColor: bgColor }}
        className="sticky top-0 h-screen w-full overflow-hidden"
      >
        {/* grain overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-multiply"
          style={{
            backgroundImage:
              'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'180\' height=\'180\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/></filter><rect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.8\'/></svg>")',
          }}
        />

        {/* Dark-phase white tint overlay for text contrast (Phases 3-4) */}
        <motion.div
          style={{ opacity: textInvert }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute inset-0 bg-black/0" />
        </motion.div>

        {/* Top progress bar */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-black/10 z-30">
          <motion.div style={{ width: barWidth }} className="h-full bg-brand-orange" />
        </div>

        {/* Phase content (stacked) */}
        <PhaseShell progress={progress} textInvert={textInvert} />
      </motion.div>
    </section>
  );
};

const PhaseShell = ({ progress, textInvert }) => {
  // We invert text/background contrast for dark phases via a wrapper class.
  // textInvert goes 0 (light bg) → 1 (dark bg).
  return (
    <motion.div
      className="absolute inset-0"
      style={{
        // When textInvert=1 (dark phase), flip page text via filter? We just rely on
        // each phase having its own contrast-aware colors. Wrapper kept for layout.
      }}
    >
      {/* During dark phases, lay a subtle dark tint so phase text remains legible */}
      <motion.div
        style={{ opacity: textInvert }}
        className="absolute inset-0 bg-black"
      />
      {/* Light-text variants overlay when on dark bg */}
      <Phase1 progress={progress} />
      <Phase2 progress={progress} />
      <Phase3 progress={progress} />
      <DarkPhaseText progress={progress} />
      <Phase4 progress={progress} />
      <Phase5 progress={progress} onCtaClick={() => {
        const el = document.getElementById('custom');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }} />
    </motion.div>
  );
};

const DarkPhaseText = ({ progress }) => {
  // Floating label/caption that appears while pipe animates (Phase 3 dark bg)
  const opacity = useTransform(progress, [0.42, 0.50, 0.62, 0.66], [0, 1, 1, 0]);
  const y = useTransform(progress, [0.42, 0.55], [40, 0]);
  return (
    <motion.div
      style={{ opacity, y }}
      className="absolute inset-x-0 top-[14vh] flex justify-center px-6 pointer-events-none"
    >
      <div className="text-center max-w-3xl">
        <p className="font-body text-xs sm:text-sm tracking-[0.4em] uppercase text-brand-orange mb-4">
          From brain → to bowl → to brand
        </p>
        <h2 className="font-display font-black text-3xl sm:text-5xl md:text-6xl leading-[1.05] text-white tracking-[-0.02em]">
          We pipe it straight from your<br/>
          <span className="bg-brand-orange text-black px-3 py-0.5 inline-block -skew-x-3">imagination</span> to the press.
        </h2>
      </div>
    </motion.div>
  );
};

const Nav = ({ onCtaClick }) => (
  <header className="fixed top-0 inset-x-0 z-50 bg-[#FFF7EE]/85 backdrop-blur-md border-b border-black/5">
    <div className="flex items-center justify-between px-6 sm:px-10 py-3">
      <a
        href="#top"
        data-testid={HOME.navBrand}
        className="flex items-center gap-3"
      >
        <img
          src="/brand/logo-mark.png"
          alt="Shirt Happens Studio"
          className="h-11 w-auto select-none"
          draggable="false"
        />
      </a>
      <nav className="hidden md:flex items-center gap-7 font-display font-bold text-xs uppercase tracking-[0.2em]">
        <a href="#shop" data-testid={HOME.navShop} className="text-black hover:text-brand-orange transition-colors">Shop</a>
        <a href="#photo-dump" data-testid={HOME.navGallery} className="text-black hover:text-brand-orange transition-colors">Photo Dump</a>
        <a href="#custom" data-testid={HOME.navCustom} className="text-black hover:text-brand-orange transition-colors">Custom</a>
        <a href="#about" data-testid={HOME.navAbout} className="text-black hover:text-brand-orange transition-colors">About</a>
        <a href="#faq" data-testid={HOME.navFaq} className="text-black hover:text-brand-orange transition-colors">FAQ</a>
      </nav>
      <button
        type="button"
        data-testid={HOME.navCta}
        onClick={onCtaClick}
        className="inline-flex items-center gap-2 font-display font-bold uppercase tracking-wide text-xs sm:text-sm bg-black text-white rounded-full px-5 py-2.5 hover:bg-brand-orange transition-colors shadow-[4px_4px_0_#FF6600] hover:shadow-[4px_4px_0_#000]"
      >
        Start a Shirt →
      </button>
    </div>
  </header>
);

const CraftSection = () => (
  <section
    id="shs-craft"
    className="relative bg-[#FFF7EE] text-black py-24 px-6 sm:px-12 overflow-hidden border-t-2 border-black/5"
  >
    {/* Floating brand mascot watermark */}
    <img
      src="/brand/mascot-bust.png"
      alt=""
      aria-hidden="true"
      className="hidden md:block pointer-events-none select-none absolute -left-20 top-10 w-72 opacity-[0.06] rotate-[-8deg]"
      draggable="false"
    />
    <div className="max-w-6xl mx-auto relative">
      <p className="font-body text-xs tracking-[0.4em] uppercase text-brand-orange mb-5">
        The Process
      </p>
      <h3 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-[-0.02em] max-w-3xl">
        Flush the idea.<br/>
        We&apos;ll handle <span className="text-brand-orange">the rest.</span>
      </h3>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
        {[
          { n: '01', t: 'Brain wave', d: "Sketch it on toilet paper. We don't judge." },
          { n: '02', t: 'We craft', d: 'Our studio cleans up the line and prints small-batch.' },
          { n: '03', t: 'You wear', d: 'Printed on Gildan Softstyle tees. Ships in 3 to 7 days.' },
        ].map((step) => (
          <div key={step.n} className="border-2 border-black rounded-2xl p-5 bg-white shadow-[6px_6px_0_#000]">
            <div className="font-display font-black text-3xl text-brand-orange">{step.n}</div>
            <div className="font-display font-bold text-lg mt-1">{step.t}</div>
            <p className="font-body text-sm text-black/70 mt-2">{step.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-black text-white px-6 sm:px-12 py-16">
    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <img src="/brand/wordmark.png" alt="Shirt Happens Studio" className="h-16 w-auto select-none" draggable="false" />
        <p className="font-body text-sm text-white/60 mt-4 max-w-md">
          Custom-made shirts born from your brightest bathroom moments. Printed slowly. Worn loudly.
        </p>
      </div>
      <div className="md:col-span-3">
        <div className="font-display font-black text-xs uppercase tracking-[0.3em] text-white/40 mb-3">Navigate</div>
        <ul className="space-y-2 font-body text-sm text-white/80">
          <li><a href="#shop" className="hover:text-brand-orange transition-colors">Shop</a></li>
          <li><a href="#photo-dump" className="hover:text-brand-orange transition-colors">Photo Dump</a></li>
          <li><a href="#custom" className="hover:text-brand-orange transition-colors">Custom request</a></li>
          <li><a href="#about" className="hover:text-brand-orange transition-colors">About</a></li>
          <li><a href="/policies/pricing" className="hover:text-brand-orange transition-colors">Pricing guide</a></li>
          <li><a href="/policies/payment" className="hover:text-brand-orange transition-colors">Payment policy</a></li>
        </ul>
      </div>
      <div className="md:col-span-4">
        <div className="font-display font-black text-xs uppercase tracking-[0.3em] text-white/40 mb-3">Studio</div>
        <div className="font-body text-sm text-white/80 space-y-1">
          <div><a href="mailto:roger.shirthappensdesigns@gmail.com" className="hover:text-brand-orange transition-colors">roger.shirthappensdesigns@gmail.com</a></div>
          <div>Mon — Sat, after coffee.</div>
          <div className="flex items-center gap-3 mt-4 text-[10px] uppercase tracking-[0.3em] text-white/40">
            <span>Apple Pay</span>
            <span aria-hidden="true">·</span>
            <span>Cash App</span>
            <span aria-hidden="true">·</span>
            <span>Card</span>
          </div>
        </div>
      </div>
    </div>

    {/* Content policy disclaimer */}
    <div
      data-testid={HOME.footerDisclaimer}
      className="max-w-6xl mx-auto mt-12 pt-6 border-t border-white/10"
    >
      <div className="font-display font-black text-xs uppercase tracking-[0.3em] text-brand-orange mb-2">
        Content Policy
      </div>
      <p className="font-body text-xs text-white/50 leading-relaxed max-w-3xl">
        Shirt Happens Studio is happy to bring just about any idea to life. To keep this studio a creative
        space I'm proud of, I won't take on designs that are vulgar, hateful, racist, or that conflict with
        my personal beliefs. I reserve the right to decline any request that falls outside those values,
        and a full refund will be issued if a paid order is rejected on these grounds.
      </p>
    </div>

    <div className="max-w-6xl mx-auto mt-8 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white/40 font-body">
      <span>© {new Date().getFullYear()} Shirt Happens Studio</span>
      <span>Made with brain waves and ceramic.</span>
    </div>
  </footer>
);

const Home = () => {
  const scrollToCustom = () => {
    const el = document.getElementById('custom');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Soft smooth-scroll feel
    document.documentElement.style.scrollBehavior = 'smooth';
    // Force initial position to top on mount (avoid browser scroll restoration)
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
    return () => { document.documentElement.style.scrollBehavior = ''; };
  }, []);

  return (
    <div id="top" className="bg-[#FFF7EE] text-black antialiased">
      <Nav onCtaClick={scrollToCustom} />
      <ScrollNarrative />
      <CraftSection />
      <ShopSection />
      <GallerySection />
      <ReviewsSection />
      <TeeodoreSection />
      <CommunityWallSection />
      <WhoWeServeSection />
      <CustomRequestSection />
      <AboutSection />
      <FAQSection />
      <Footer />
      <TeeodoreCompanion />
      <RecoveryAnnouncementModal onFutureOrder={scrollToCustom} />
    </div>
  );
};

export default Home;
